import { Flex, NumberInput, Select, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";

interface TimeEstimateInputProps {
  label: string;
  tooltip?: string;
  value: string | null;
  onChange: (value: string) => void;
}

/**
 * Component nhập thời gian ước tính giao hàng
 * UI cấu trúc: [Số min] - [Số max] [Đơn vị: h làm việc / ngày]
 * Tự động ghép thành chuỗi: "2-4h làm việc" hoặc "1-3 ngày"
 */
const TimeEstimateInput = ({
  label,
  tooltip,
  value,
  onChange,
}: TimeEstimateInputProps) => {
  const [min, setMin] = useState<number | "">(0);
  const [max, setMax] = useState<number | "">(0);
  const [unit, setUnit] = useState<string>("h làm việc");

  // Parse giá trị ban đầu từ chuỗi
  useEffect(() => {
    if (!value || value.length === 0) return;

    const lower = value.toLowerCase();
    const numbers = value.match(/\d+/g);

    if (numbers && numbers.length >= 2) {
      setMin(parseInt(numbers[0]));
      setMax(parseInt(numbers[1]));
    } else if (numbers && numbers.length === 1) {
      setMin(parseInt(numbers[0]));
      setMax(parseInt(numbers[0]));
    }

    if (lower.includes("h") || lower.includes("giờ")) {
      setUnit("h làm việc");
    } else if (lower.includes("ngày") || lower.includes("ngay")) {
      setUnit(" ngày");
    }
  }, []);

  // Ghép chuỗi khi thay đổi
  const buildValue = (
    newMin: number | "",
    newMax: number | "",
    newUnit: string
  ) => {
    const minVal = newMin === "" ? 0 : newMin;
    const maxVal = newMax === "" ? 0 : newMax;
    const text = `${minVal}-${maxVal}${newUnit}`;
    onChange(text);
  };

  return (
    <div>
      <Flex align="center" gap={4} mb={4}>
        <Text size="sm" fw={500}>
          {label}
        </Text>
        {tooltip && (
          <Tooltip label={tooltip} multiline w={300} withArrow position="top">
            <IconInfoCircle
              size={14}
              color="#868e96"
              style={{ cursor: "pointer" }}
            />
          </Tooltip>
        )}
      </Flex>
      <Flex gap="xs" align="center">
        <NumberInput
          value={min}
          onChange={(val) => {
            const v = val === "" ? "" : Number(val);
            setMin(v);
            buildValue(v, max, unit);
          }}
          min={0}
          max={99}
          hideControls
          allowNegative={false}
          style={{ flex: 1 }}
          placeholder="Min"
        />
        <Text size="sm" c="dimmed" fw={500}>
          —
        </Text>
        <NumberInput
          value={max}
          onChange={(val) => {
            const v = val === "" ? "" : Number(val);
            setMax(v);
            buildValue(min, v, unit);
          }}
          min={0}
          max={99}
          hideControls
          allowNegative={false}
          style={{ flex: 1 }}
          placeholder="Max"
        />
        <Select
          value={unit}
          onChange={(val) => {
            const newUnit = val || "h làm việc";
            setUnit(newUnit);
            buildValue(min, max, newUnit);
          }}
          data={[
            { label: "Giờ LV", value: "h làm việc" },
            { label: "Ngày", value: " ngày" },
          ]}
          allowDeselect={false}
          style={{ flex: 2 }}
        />
      </Flex>
    </div>
  );
};

export default TimeEstimateInput;
